import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, LinearProgress, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useOnboardingAuth } from '../../hooks/useOnboardingAuth';
import { occasionsService } from '../../services/occasionsService';
import { vendorOccasionsService } from '../../services/vendorOccasionsService';

// S3 asset URL
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

const OnboardingStep6: React.FC = () => {
  usePageTitle('Occasions Setup');
  const navigate = useNavigate();
  const { vendor, isLoading: authLoading } = useOnboardingAuth({ currentStep: 5 });

  const [occasions, setOccasions] = useState<Array<{ occasionId: string; title: string }>>([]);
  const [selectedOccasions, setSelectedOccasions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await occasionsService.getAllOccasions();
        setOccasions((res.data.occasions || []).map(o => ({ occasionId: o.occasionId, title: o.title })));
      } catch (e) {
        toast.error('Failed to load occasions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleOccasion = (id: string) => {
    setSelectedOccasions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendor) {
      toast.error('No vendor session found. Please try again.');
      return;
    }
    if (selectedOccasions.length === 0) {
      toast.error('Please select at least one occasion');
      return;
    }
    setIsSubmitting(true);
    try {
      const resp = await vendorOccasionsService.saveOccasions({ vendorId: vendor.vendorId, occasionIds: selectedOccasions });
      if (resp.success) {
        toast.success('Occasions saved! Redirecting...');
        setTimeout(() => navigate('/company-profile'), 1200);
      } else {
        toast.error(resp.error || 'Failed to save occasions');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to save occasions';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/onboarding/step5');
  };

  const progress = 100; // 6 of 6

  if (authLoading || loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', width: '100vw' }}>
        <CircularProgress size={60} sx={{ color: '#67314f', marginBottom: '20px' }} />
        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 400, fontSize: '16px', color: '#181818', textAlign: 'center' }}>Loading...</Typography>
      </Box>
    );
  }

  if (!vendor) return null;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0', width: '100vw' }}>
      <Box sx={{ width: '120px', height: '94px', marginBottom: '40px' }}>
        <Box component="img" src={logoImg} alt="Eventi Logo" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px', width: '100%', maxWidth: '540px', padding: '0 20px' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 700, fontSize: '44px', lineHeight: 1.15, letterSpacing: '-0.44px', color: '#181818', marginBottom: '16px', textAlign: 'left' }}>
            Select occasions
          </Typography>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 400, fontSize: '16px', lineHeight: 1.4, color: '#181818', marginBottom: '16px', textAlign: 'left' }}>
            Choose the occasions you serve so customers can find you more easily.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 400, fontSize: '14px', color: '#181818' }}>
              6 of 6
            </Typography>
          </Box>
          <Box sx={{ width: '100%', margin: '0 auto' }}>
            <LinearProgress variant="determinate" value={progress} sx={{ height: '4px', borderRadius: '2px', backgroundColor: '#f0f0f0', '& .MuiLinearProgress-bar': { backgroundColor: '#67314f', borderRadius: '2px' } }} />
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '320px', overflowY: 'auto', pr: 1 }}>
            {occasions.map(o => (
              <FormControlLabel key={o.occasionId} control={<Checkbox checked={selectedOccasions.includes(o.occasionId)} onChange={() => toggleOccasion(o.occasionId)} />} label={o.title} />
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            <Button type="submit" fullWidth disabled={isSubmitting} sx={{ height: '56px', backgroundColor: '#67314f', borderRadius: '16px', fontFamily: '"Inter", sans-serif', fontWeight: 700, fontSize: '16px', letterSpacing: '-0.32px', textTransform: 'none', color: 'white', '&:hover': { backgroundColor: '#5a2a42' }, '&:disabled': { backgroundColor: '#93989c', color: 'white' } }}>
              {isSubmitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Complete Setup'}
            </Button>
            <Button onClick={handleBack} fullWidth startIcon={<ArrowBack />} sx={{ height: '56px', backgroundColor: 'transparent', borderRadius: '16px', fontFamily: '"Inter", sans-serif', fontWeight: 700, fontSize: '16px', letterSpacing: '-0.32px', textTransform: 'none', color: '#67314f', '&:hover': { backgroundColor: 'rgba(103, 49, 79, 0.04)' }, '& .MuiButton-startIcon': { marginRight: '8px' } }}>
              Back
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingStep6;
